import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import {
  clearRumUserIdentity,
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  setRumUserIdentityFromIdToken,
} from '../utils/rum';

interface ProtectedRouteProps {
  children: React.ReactNode;
  onSessionExpired: () => void;
}

type ProtectedSessionContextValue = {
  username: string | null;
  logout: () => Promise<void>;
};

const ProtectedSessionContext = createContext<ProtectedSessionContextValue | null>(null);

export function useOptionalProtectedSession(): ProtectedSessionContextValue | null {
  return useContext(ProtectedSessionContext);
}

export function useProtectedSession(): ProtectedSessionContextValue {
  const context = useOptionalProtectedSession();
  if (!context) {
    throw new Error('useProtectedSession must be used within ProtectedRoute.');
  }
  return context;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, onSessionExpired }) => {
  const navigate = useNavigate();
  const [sessionValid, setSessionValid] = useState(false);
  const [username, setUsername] = useState<string | null>(null);

  function parseJwtPayload(token: string): Record<string, any> | null {
    try {
      const [, payload] = token.split('.');
      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      return JSON.parse(atob(normalized));
    } catch {
      return null;
    }
  }

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      const startedAt = performance.now();
      recordFunnelStep('protected_session_check', 'start');
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString() || '';
        const accessToken = session.tokens?.accessToken?.toString() || '';
        const hasSession = Boolean(idToken || accessToken);
        const claims = idToken ? parseJwtPayload(idToken) : null;

        if (!isMounted) {
          return;
        }

        setSessionValid(hasSession);
        const resolvedUsername =
          (typeof claims?.email === 'string' && claims.email) ||
          (typeof claims?.['cognito:username'] === 'string' && claims['cognito:username']) ||
          null;
        setUsername(resolvedUsername);
        if (hasSession && idToken) {
          setRumUserIdentityFromIdToken(idToken);
        } else {
          clearRumUserIdentity();
        }
        if (!hasSession) {
          recordFunnelStep('protected_session_check', 'failure', {
            reason: 'missing_tokens',
          });
          onSessionExpired();
        } else {
          recordFunnelStep('protected_session_check', 'success');
        }
        recordLatency('protected_session_check', performance.now() - startedAt);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setSessionValid(false);
        setUsername(null);
        clearRumUserIdentity();
        recordFunnelStep('protected_session_check', 'failure', {
          reason: 'fetch_auth_session_error',
        });
        recordErrorStage('protected_session_check', error);
        onSessionExpired();
      }
    };

    checkSession();

    return () => {
      isMounted = false;
    };
  }, [onSessionExpired]);

  const handleLogout = useCallback(async () => {
    recordFunnelStep('logout', 'start');
    try {
      await signOut();
      clearRumUserIdentity();
      recordFunnelStep('logout', 'success');
    } catch (error) {
      console.error('Sign out failed:', error);
      recordFunnelStep('logout', 'failure');
      recordErrorStage('logout', error);
    }
    onSessionExpired();
    navigate('/', { replace: true });
  }, [navigate, onSessionExpired]);

  const sessionValue = {
    username,
    logout: handleLogout,
  };

  if (!sessionValid) {
    return null;
  }

  return <ProtectedSessionContext.Provider value={sessionValue}>{children}</ProtectedSessionContext.Provider>;
};
